import Illusion from 'illusionjs'
import Relationship from './Relationships'
import Collection, { Collection as CollectionClass } from './Collection'
import QueryBuilder from './QueryBuilder'
import Model from './Model'
import { PersistentRelative } from './types'
import { ConnectionPool } from './Connection'

type WithRelativeConstraints = (relativeModel?: any, relativeName?: string) => any

export class Builder {

    protected _query: QueryBuilder

    protected _connectionPool?: ConnectionPool | null = null

    protected _model: Model

    protected _modelName: string

    protected _persistentLoad: PersistentRelative<any, any> = {}

    protected _factoryMethodThatCanReturn: string[] = ['insert', 'delete', 'update', 'count', 'now']

    constructor(query: QueryBuilder, model: any) {

        this._query = query

        this._model = model

        this._modelName = model.constructor.name
    }

    protected filterPersistentLoads(callback: (relative: string) => boolean, offset: number = 0): PersistentRelative<any, any> | undefined {
        const persistentLoads = this._persistentLoad

        const result: PersistentRelative<any, any> | undefined = Object.keys(persistentLoads)
            .filter(callback)
            .reduce((result: PersistentRelative<any, any> = {}, relative: string) => {

                result[relative.substring(offset)] = persistentLoads[relative]
                
                return result
            }, undefined)

        return result
    }

    protected unnestedPersistentLoads(): PersistentRelative<any, any> | undefined {

        return this.filterPersistentLoads((relative) => !relative.includes('.'))
    }

    protected async persistentLoadRelations(models: Model[]): Promise<any[]> {

        const unnestedPersistentLoads = this.unnestedPersistentLoads()

        if(unnestedPersistentLoads) {

            await Promise.all(Object.keys(unnestedPersistentLoads).map((relative: string) => (
                this.loadRelationship(models, relative, unnestedPersistentLoads[relative])
            )))
        }

        return models
    }

    protected async loadRelationship(models: Model[], relative: string, relativeConstraints: WithRelativeConstraints) {
        const relativeWithBuilder = this.getRelative(relative)
        
        await relativeWithBuilder.addPersistentConstraints(models)

        relativeConstraints.call(relativeWithBuilder, relativeWithBuilder)

        const relativeModels = await relativeWithBuilder.get()

        return relativeWithBuilder.match(models, relativeModels, relative)
    }

    protected async persistentDeleteRelations(models: Model[], soft: boolean): Promise<any> {

        const unnestedPersistentLoads = this.unnestedPersistentLoads()

        if(unnestedPersistentLoads) {

            const persistentLoadsKeys = Object.keys(unnestedPersistentLoads)

            const relativeDeletes: any[] = await Promise.all(persistentLoadsKeys.map((relative: string) => (
                this.deleteRelationship(models, relative, unnestedPersistentLoads[relative], soft)
            )))
            
            return relativeDeletes.reduce((result: any, deleted: any[], index: number) => {

                result[persistentLoadsKeys[index]] = deleted

                return result
            }, {})
        }
    }

    protected async deleteRelationship(models: Model[], relative: string, relativeConstraints: WithRelativeConstraints, soft: boolean) {
        
        const relativeWithBuilder = this.getRelative(relative)
        
        if('addPersistentConstraintsForDelete' in relativeWithBuilder) {

            await relativeWithBuilder.addPersistentConstraintsForDelete(models)

        } else {
            
            await relativeWithBuilder.addPersistentConstraints(models)
        }

        relativeConstraints.call(relativeWithBuilder, relativeWithBuilder)

        if(soft) {

            return relativeWithBuilder.softDelete()
        } else {

            return relativeWithBuilder.delete()
        }
    }

    protected async persistentRestoreRelations(models: Model[]): Promise<any> {

        const unnestedPersistentLoads = this.unnestedPersistentLoads()

        if(unnestedPersistentLoads) {

            const persistentLoadsKeys = Object.keys(unnestedPersistentLoads)

            const relativeRestores: any[] = await Promise.all(persistentLoadsKeys.map((relative: string) => (
                this.restoreRelationship(models, relative, unnestedPersistentLoads[relative])
            )))
            
            return relativeRestores.reduce((result: any, restored: any[], index: number) => {

                result[persistentLoadsKeys[index]] = restored

                return result
            }, {})
        }
    }

    protected async restoreRelationship(models: Model[], relative: string, relativeConstraints: WithRelativeConstraints) {
        
        const relativeWithBuilder = this.getRelative(relative)
        
        if('addPersistentConstraintsForRestore' in relativeWithBuilder) {

            await relativeWithBuilder.addPersistentConstraintsForRestore(models)

        } else {
            
            await relativeWithBuilder.addPersistentConstraints(models)
        }

        relativeConstraints.call(relativeWithBuilder, relativeWithBuilder)

        return relativeWithBuilder.restore()
    }

    protected getRelative(relative: string) {
        
        const relativeWithBuilder = Relationship.noConstraints(() => {

            const model = this.getModel() as any;

            if(typeof model[relative] === 'function') {
                return model[relative]()
            } else {
                throw Error(`[${model.constructor.name}] model cannot find relationship [${relative}]`)
            }
        })

        if(this._connectionPool) {

            relativeWithBuilder.pool(this._connectionPool)
        }

        const thirdPartyRelations = this.thirdPartyRelations(relative)
        
        if(thirdPartyRelations) {

            relativeWithBuilder.with(thirdPartyRelations)
        }

        return relativeWithBuilder
    }

    protected thirdPartyRelations(currentRelative: string): PersistentRelative<any, any> | undefined {

        const offset = `${currentRelative}.`.length

        const getCurrentRelativeNested = this.filterPersistentLoads((relative) => (

            relative.includes(currentRelative) && relative.includes('.')
        ), offset)

        return getCurrentRelativeNested
    }

    protected normalizePersistentLoad(relatives: PersistentRelative<any, any>) {

        return Object.keys(relatives).reduce((result: any, key: string) => {

            const constraints: WithRelativeConstraints = relatives[key]

            if(key.includes('.')) {

                key.split('.').forEach((_, i: number, array: string[]) => {
                    const isLast = i === array.length - 1
                    const key = array.slice(0, i + 1).join('.')
                    // this condition to prevent overiding declared constraints
                    if(!(key in relatives)) { // fix override if it is already existed on relatives
                        result[key] = isLast ? constraints : () => {}
                    }
                })

            } else {
                result[key] = constraints
            }

            return result
        }, {})
    }

    protected setPersistentLoad(relatives: PersistentRelative<any, any>) {

        const persistentLoad = this.normalizePersistentLoad(relatives)

        this._persistentLoad = persistentLoad
    }

    public with(relatives: string | string[] | PersistentRelative<any, any>): this {
        
        if(typeof relatives === 'string') {

            relatives = { [relatives]: () => {} }
        } else if(Array.isArray(relatives)) {

            relatives = relatives.reduce((result: PersistentRelative<any, any>, relative: string) => {

                result[relative] = () => {}
                
                return result
            }, {})
        }

        this.setPersistentLoad(relatives)

        return this
    }

    public getPool(): ConnectionPool | null | undefined {

        return this._connectionPool
    }

    public pool(pool: ConnectionPool): this {

        this._connectionPool = pool

        this.getQueryBuilder().pool(pool)

        return this
    }

    public findAndDelete(find: string | number, relatives?: string | string[] | any) : Promise<{ itemsDeleted: number, relativeDeleted?: any}> {

        this.getQueryBuilder().where(this.getModel().getPrimaryKey(), find)

        if(relatives) {
            this.with(relatives)
        }

        return this.delete()
    }

    public findAndSoftDelete(find: string | number, relatives?: string | string[] | any) : Promise<{ itemsSoftDeleted: number, relativeSoftDeleted?: any}> {

        this.getQueryBuilder().where(this.getModel().getPrimaryKey(), find)

        if(relatives) {
            this.with(relatives)
        }

        return this.softDelete()
    }

    public findAndRestore(find: string | number, relatives?: string | string[] | any) : Promise<{ itemsRestored: number, relativeRestored?: any}> {

        this.getQueryBuilder().where(this.getModel().getPrimaryKey(), find)

        if(relatives) {
            this.with(relatives)
        }

        return this.restore()
    }

    public find(find: string | number, column: string[], relatives?: string | string[] | any) : Promise<CollectionClass> {
        
        const [selectOrSub, ...select] = column

        this.getQueryBuilder().select(selectOrSub, ...select).where(this.getModel().getPrimaryKey(), find)

        if(relatives) {
            this.with(relatives)
        }

        return this.first()
    }

    public async first(): Promise<CollectionClass> {
        try {

            const first = await this.getQueryBuilder().first()

            if(first) {

                await this.persistentLoadRelations([first])
            }
            
            return new Collection(first)

        } catch(error) {
        
            throw new Error(error.message)
        } 
    }

    public async get(): Promise<CollectionClass> {
        try {
            const models = await this.getQueryBuilder().get()

            if(models.length) {

                await this.persistentLoadRelations(models)
            }

            return new Collection(models)

        } catch(error) {
        
            throw new Error(error.message)
        } 
    }

    public async paginate(page: number, size: number): Promise<CollectionClass> {
        try {
            const {models, count} = await this.getQueryBuilder().paginate(page, size)

            if(models.length) {

                await this.persistentLoadRelations(models)
            }

            return new Collection(models, count)
            
        } catch(error) {
        
            throw new Error(error.message)
        } 
    }

    public async delete(): Promise<{ itemsDeleted: number, relativeDeleted?: any}> {
        try {

            if(!this.getModel().isDeletable()) {

                throw new Error(`${this.getModel().getConstructor().name} is not deletable`)
            }

            const models = await this.getQueryBuilder().get()

            let relativeDeleted
            
            if(models.length && Object.keys(this._persistentLoad).length) {

                relativeDeleted = await this.persistentDeleteRelations(models, false)
            }

            const deleted = await this.getQueryBuilder().delete()

            return { itemsDeleted: deleted, relativeDeleted }

        } catch(error) {
        
            throw new Error(error.message)
        } 
    }

    public async softDelete(): Promise<{ itemsSoftDeleted: number, relativeSoftDeleted?: any}> {

        try {

            if(!this.getModel().isDeletable()) {

                throw new Error(`${this.getModel().getConstructor().name} is not deletable`)
            }

            const models = await this.getQueryBuilder().get()

            let relativeSoftDeleted
            
            if(models.length && Object.keys(this._persistentLoad).length) {

                relativeSoftDeleted = await this.persistentDeleteRelations(models, true)
            }

            const deleted = await this.getQueryBuilder().softDelete()

            return { itemsSoftDeleted: deleted, relativeSoftDeleted }

        } catch(error) {
        
            throw new Error(error.message)
        } 
        
    }

    public async restore(): Promise<any> {
        try {

            const restored = await this.getQueryBuilder().restore()

            const models = await this.getQueryBuilder().get()

            let relativeRestored
            
            if(models.length && Object.keys(this._persistentLoad).length) {

                relativeRestored = await this.persistentRestoreRelations(models)
            }

            return { itemsRestored: restored, relativeRestored }

        } catch(error) {
        
            throw new Error(error.message)
        } 

    }

    protected isReturnableFactoryMethod(method: string): boolean {

        return this._factoryMethodThatCanReturn.includes(method)
    }

    public getQuery(): string {
        return this._query.getQuery()
    }

    public getParameters(): Record<string, any> {

        return this._query.getParameters()
    }

    public getQueryBuilder(): QueryBuilder {

        return this._query
    }

    public getModel(): Model {
        
        return this._model
    }

    __get(method: string) {
        
        const modelName = this._modelName

        if (typeof method === 'symbol') 
            
            return () => `do not use console.log(${modelName}) use instead console.log(${modelName}.toJson())`
		
		const query = this.getQueryBuilder() as any

		if (!(method in query)) 
		{
			throw new Error(`${modelName}.${method} does not exists`)
		}

		return (...parameters: any[]) => {

			const result = query[method](...parameters)
			
			return this.isReturnableFactoryMethod(method) ? result : this
		}
	}
}

export default Illusion<typeof Builder>(Builder)